import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Youtube, Eye, CalendarDays, ExternalLink, Clapperboard, Smartphone, Film } from "lucide-react";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchVideos, cleanTitle, fmtViews, thumb, CHANNEL_URL } from "@/lib/youtube";

const TABS = [
  { key: "all", label: "All videos", icon: Clapperboard },
  { key: "shorts", label: "Shorts", icon: Smartphone },
  { key: "films", label: "Travel films", icon: Film },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Player = ({ video, playing, onStart }) => (
  <div className={`relative mx-auto overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)] ${video.short ? "aspect-[9/16] h-[60vh] sm:h-[70vh] max-h-[720px] max-w-full" : "aspect-video w-full"}`} data-testid="video-player">
    {playing ? (
      <iframe
        key={video.id}
        src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`}
        referrerPolicy="strict-origin-when-cross-origin"
        title={cleanTitle(video.title)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        data-testid="video-iframe"
      />
    ) : (
      <button type="button" onClick={onStart} className="group absolute inset-0 w-full h-full" data-testid="video-poster-play" aria-label="Play video">
        <img src={thumb(video.id)} alt={cleanTitle(video.title)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />
        <span className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-[#ff0033] text-white flex items-center justify-center shadow-[0_0_0_12px_rgba(255,0,51,0.2)] group-hover:scale-110 transition-transform duration-300">
          <Play className="w-8 h-8 ml-1 fill-current" />
        </span>
      </button>
    )}
  </div>
);

const NowPlaying = ({ video }) => (
  <div className="text-white" data-testid="now-playing">
    <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--yellow)] font-semibold">Now playing</div>
    <h2 className="font-serif-display text-2xl md:text-3xl mt-3 leading-snug" data-testid="now-playing-title">{cleanTitle(video.title)}</h2>
    <div className="flex flex-wrap gap-4 mt-4 text-[13px] text-white/70">
      <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtDate(video.published)}</span>
      <span className="inline-flex items-center gap-1.5"><Eye className="w-4 h-4" />{fmtViews(video.views)} views</span>
      {video.short && <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold">SHORT</span>}
    </div>
    {video.description && <p className="mt-4 text-[14px] text-white/60 line-clamp-4 whitespace-pre-line">{video.description.replace(/#\S+/g, "").trim()}</p>}
    <div className="flex flex-wrap gap-3 mt-6">
      <a href={`https://www.youtube.com/watch?v=${video.id}`} target="_blank" rel="noreferrer" data-testid="watch-on-youtube" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-[13px] font-semibold hover:bg-white hover:text-[var(--navy)] transition-colors">
        Watch on YouTube <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <a href={`${CHANNEL_URL}?sub_confirmation=1`} target="_blank" rel="noreferrer" data-testid="subscribe-btn" className="inline-flex items-center gap-2 rounded-full bg-[#ff0033] px-4 py-2 text-[13px] font-semibold hover:bg-[#d9002b] transition-colors">
        <Youtube className="w-4 h-4" /> Subscribe
      </a>
    </div>
  </div>
);

const VideoCard = ({ video, active, onPlay, index }) => (
  <motion.button
    type="button"
    onClick={() => onPlay(video)}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.45, delay: (index % 4) * 0.06 }}
    className="group text-left w-full"
    data-testid={`video-card-${video.id}`}
  >
    <div className={`relative overflow-hidden rounded-xl bg-[var(--navy)] ring-2 transition-[box-shadow,transform] duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_20px_40px_-18px_rgba(11,37,69,0.6)] ${active ? "ring-[var(--yellow)]" : "ring-transparent"} ${video.short ? "aspect-[9/16]" : "aspect-video"}`}>
      <img src={thumb(video.id)} alt={cleanTitle(video.title)} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0" />
      <span className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-white/90 text-[var(--navy)] flex items-center justify-center scale-90 opacity-90 group-hover:scale-100 group-hover:bg-[var(--yellow)] transition-[transform,background-color] duration-300">
        <Play className="w-5 h-5 ml-0.5 fill-current" />
      </span>
      {active && <span className="absolute top-2 left-2 rounded-full bg-[var(--yellow)] text-[var(--navy)] text-[10px] font-bold px-2 py-0.5">PLAYING</span>}
      {video.short && <span className="absolute top-2 right-2 rounded bg-[#ff0033] text-white text-[10px] font-bold px-1.5 py-0.5">SHORT</span>}
      <span className="absolute bottom-2 left-2 text-white/85 text-[11px] inline-flex items-center gap-1"><Eye className="w-3 h-3" />{fmtViews(video.views)}</span>
    </div>
    <div className="mt-2.5 text-[13.5px] font-semibold text-[var(--navy)] line-clamp-2 group-hover:text-[var(--red)] transition-colors">{cleanTitle(video.title)}</div>
    <div className="text-[12px] text-gray-500 mt-0.5">{fmtDate(video.published)}</div>
  </motion.button>
);

const VideoGroup = ({ title, icon: Icon, items, current, onPlay }) => items.length > 0 && (
  <div data-testid={`video-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-5 text-[13px] uppercase tracking-[0.2em] font-semibold text-[#0b2545]/70"><Icon className="w-4 h-4 text-[#ff0033]" /> {title}</div>
    <div className={`grid gap-x-5 gap-y-8 items-start ${items[0].short ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
      {items.map((v, i) => <VideoCard key={v.id} video={v} index={i} active={current?.id === v.id} onPlay={onPlay} />)}
    </div>
  </div>
);

const Tabs = ({ tab, setTab, counts }) => (
  <div className="inline-flex gap-1 bg-white border border-gray-200 rounded-full p-1 max-w-full overflow-x-auto [scrollbar-width:none]" data-testid="video-tabs">
    {TABS.map(({ key, label, icon: Icon }) => (
      <button key={key} type="button" onClick={() => setTab(key)} data-testid={`video-tab-${key}`} className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-[12.5px] sm:text-[13px] font-semibold transition-colors ${tab === key ? "bg-[var(--navy)] text-white" : "text-[#0b2545]/70 hover:text-[var(--navy)]"}`}>
        <Icon className="w-4 h-4" /> {label} <span className="opacity-60">({counts[key]})</span>
      </button>
    ))}
  </div>
);

export default function VideosPage() {
  const [videos, setVideos] = useState(null);
  const [error, setError] = useState(false);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState("all");
  const [params] = useSearchParams();

  useEffect(() => {
    fetchVideos().then((list) => {
      const picked = list.find((v) => v.id === params.get("v"));
      setVideos(list);
      setCurrent(picked || list.find((v) => !v.short) || list[0]);
      if (picked) setPlaying(true);
    }).catch(() => setError(true));
  }, [params]);

  const counts = useMemo(() => ({
    all: videos?.length || 0,
    shorts: videos?.filter((v) => v.short).length || 0,
    films: videos?.filter((v) => !v.short).length || 0,
  }), [videos]);
  const list = (videos || []).filter((v) => tab === "all" || (tab === "shorts" ? v.short : !v.short));

  const play = (v) => {
    setCurrent(v);
    setPlaying(true);
    document.getElementById("video-stage")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden" data-testid="videos-page">
      <Header />
      <section id="video-stage" className="relative bg-[#07182e] scroll-mt-32">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none bg-[radial-gradient(circle_at_20%_20%,#f5b800_0,transparent_40%),radial-gradient(circle_at_80%_80%,#ff0033_0,transparent_35%)]" />
        <div className="relative max-w-[1280px] mx-auto px-4 lg:px-6 py-12 lg:py-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[var(--yellow)]"><Youtube className="w-4 h-4" /> Royal Holidays on YouTube</div>
              <h1 className="font-serif-display text-4xl sm:text-5xl text-white mt-2" data-testid="videos-title">Travel stories in motion</h1>
              <p className="text-white/60 mt-3 max-w-xl text-[14px]">Destination reels, guest reviews and tour films from our travellers around the world.</p>
            </div>
            <a href={CHANNEL_URL} target="_blank" rel="noreferrer" data-testid="visit-channel-btn" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--navy)] px-5 py-2.5 text-[13px] font-bold hover:bg-[var(--yellow)] transition-colors">
              <Youtube className="w-4 h-4 text-[#ff0033]" /> @royalholidayspvt
            </a>
          </div>
          {error ? (
            <div className="rounded-2xl border border-white/10 p-10 text-center text-white/70" data-testid="videos-error">
              Videos couldn't load right now. <a href={CHANNEL_URL} target="_blank" rel="noreferrer" className="underline text-white font-semibold">Watch them on our YouTube channel</a>.
            </div>
          ) : !current ? (
            <Skeleton className="w-full aspect-video rounded-2xl bg-white/10" data-testid="videos-loading" />
          ) : (
            <div className={`grid gap-8 lg:gap-12 items-center ${current.short ? "lg:grid-cols-[minmax(0,420px)_1fr]" : "lg:grid-cols-[1.6fr_1fr]"}`}>
              <Player video={current} playing={playing} onStart={() => setPlaying(true)} />
              <NowPlaying video={current} />
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--soft)] py-12">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="font-serif-display text-2xl md:text-3xl">Latest from the channel</h2>
            <Tabs tab={tab} setTab={setTab} counts={counts} />
          </div>
          {!videos && !error ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">{[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-video rounded-xl" />)}</div>
          ) : (
            <div className="space-y-12" data-testid="video-grid">
              {tab !== "films" && <VideoGroup title="Shorts" icon={Smartphone} items={list.filter((v) => v.short)} current={current} onPlay={play} />}
              {tab !== "shorts" && <VideoGroup title="Travel films" icon={Film} items={list.filter((v) => !v.short)} current={current} onPlay={play} />}
            </div>
          )}
          <div className="mt-12 text-center">
            <a href={`${CHANNEL_URL}/videos`} target="_blank" rel="noreferrer" data-testid="see-all-videos-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--navy)] text-white px-6 py-3 text-[14px] font-bold hover:bg-[var(--red)] transition-colors">
              See all videos on YouTube <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
      <Footer />
      <FloatingWidgets />
    </div>
  );
}
